import { DataType, type Table } from 'apache-arrow';

import type { ColumnType } from '../types/reporting';

export type ResultRow = Record<string, unknown>;

// Collapse an Arrow field type into the reporting ColumnType vocabulary so the
// extract-schema probe (duckdbEngine.getDatasetColumns) can hand
// validateDefinition manifest entries typed by EXTRACT reality. Mirrors
// normalizeValue's discrimination below: dates/timestamps render as dates,
// ints/floats/decimals as numbers, bools as booleans, everything else as
// strings (the manifest's own fallback type).
export function arrowFieldTypeToColumnType(type: DataType): ColumnType {
  if (DataType.isDate(type) || DataType.isTimestamp(type)) return 'date';
  if (
    DataType.isInt(type) ||
    DataType.isFloat(type) ||
    DataType.isDecimal(type)
  ) {
    return 'number';
  }
  if (DataType.isBool(type)) return 'boolean';
  return 'string';
}

function normalizeValue(value: unknown, type: DataType): unknown {
  if (value === null || value === undefined) return null;
  if (DataType.isDate(type) || DataType.isTimestamp(type)) {
    const ms = value instanceof Date ? value.getTime() : Number(value);
    return new Date(ms).toISOString().slice(0, 10);
  }
  // apache-arrow surfaces a DECIMAL(p,s) as an UNSCALED integer (a DecimalBigNum
  // backed by an Int128), so Number(value) yields e.g. 9990 for a stored 99.90.
  // Divide by 10**scale to recover the real fractional value. Money aggregates
  // arrive as DECIMAL because the compiler wraps them in CAST(... AS
  // DECIMAL(18,2)) for cent-exact summation (compileReport.ts measureExpr);
  // without this scaling those sum/min/max/avg columns render 100x too large
  // end-to-end (table cells and CSV exports). Checked before the bigint branch
  // because a decimal value can arrive as a bigint too.
  if (DataType.isDecimal(type)) return Number(value) / 10 ** type.scale;
  if (typeof value === 'bigint') return Number(value);
  return value;
}

export function arrowToRows(table: Table): ResultRow[] {
  const fields = table.schema.fields;
  const rows: ResultRow[] = [];
  for (const proxy of table) {
    const json = proxy.toJSON() as Record<string, unknown>;
    const row: ResultRow = {};
    for (const field of fields) {
      row[field.name] = normalizeValue(json[field.name], field.type);
    }
    rows.push(row);
  }
  return rows;
}
